import { useState, useEffect } from "react";
import { t } from "copy/t";
import { useGetGallerySessionToken } from "../api/mutations";
import Input from "components/TailwindComponents/Input";
import { ExclamationCircleIcon } from "@heroicons/react/20/solid";
import Logo from "components/Logo";
import {
  getAccessibleTextColor,
  getErrorAccentColor,
  getSecondaryTextColor,
} from "utils/helpers";
import LegalLinks from "./LegalLinks";

const MESSAGE_ID = "gallery-gate-message";
// rails locks a gallery for 10 minutes at the 10th failed attempt (the first
// lockout; later ones run 5, 10, 15... minutes, UnlockChannelAttempt), and its
// 429 names no time ("Try again later."), so submit stays off for the first
// lockout's length. A retry after that which is still locked out re-disables it.
export const LOCKOUT_COOLDOWN_MS = 10 * 60 * 1000;

/**
 * `onUnlocked`, when given, is called once the unlock token is stored, in place
 * of the full-page reload the gallery uses. The single-photo page passes it so
 * it can refetch that one photo with the new token without reloading.
 *
 * `logoUrl`, `headline` and `headlineColor` are the channel's own hero logo,
 * headline and headline colour, already on the channel payload; without a logo
 * the Movebooth mark shows, and without a headline the gate title leads.
 */
const GalleryGate = ({
  channelId,
  galleryColor,
  onUnlocked,
  logoUrl,
  headline,
  headlineColor,
}) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [lockoutMessage, setLockoutMessage] = useState(null);
  const [lockoutAt, setLockoutAt] = useState(0);
  const [lockedOut, setLockedOut] = useState(false);
  const tokenMutation = useGetGallerySessionToken(channelId, password);

  const handleSubmit = async (event) => {
    event.preventDefault();
    tokenMutation.mutate();
  };

  useEffect(() => {
    if (tokenMutation.isSuccess) {
      const { data } = tokenMutation;
      if (data.error) {
        setError(data.error);
      } else if (data.lockoutMessage) {
        setLockoutMessage(data.lockoutMessage);
        setLockoutAt(Date.now());
      } else if (data.success && data.unlock_token) {
        // V3 API returns { success: true, unlock_token: "..." }
        // Token is stored in mutation's onSuccess handler.
        // Hand control back to the caller, or reload the page to use the new token.
        if (onUnlocked) {
          onUnlocked();
        } else {
          window.location.reload();
        }
      } else {
        // Unexpected response shape — don't reload (would loop); show a generic error
        setError(t("galleryGate.unexpectedError"));
      }
    }
    // `data` is read above, so it has to be a dependency: a second submission
    // that resolves while `isSuccess` is still true would otherwise leave this
    // acting on the previous response.
    // `onUnlocked` is deliberately not a dependency: a parent re-render that
    // hands in a new callback must not re-fire the unlock for the same response.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenMutation.isSuccess, tokenMutation.data]);

  // Handle API errors (401 Unauthorized, 429 Too Many Requests, etc.)
  useEffect(() => {
    if (tokenMutation.isError) {
      const errorResponse = tokenMutation.error?.response?.data;
      if (errorResponse?.error) {
        // Check if it's a lockout error (429 Too Many Requests)
        if (tokenMutation.error?.response?.status === 429) {
          setLockoutMessage(errorResponse.error);
          setLockoutAt(Date.now());
          setError(null);
        } else {
          setError(t("galleryGate.incorrectPassword"));
          setLockoutMessage(null);
        }
      } else {
        setError(t("galleryGate.genericError"));
      }
    }
  }, [tokenMutation.isError, tokenMutation.error]);

  // A lockout disables submit for a cooldown: the server names no window, and
  // every retry inside it would fail. Keyed on the time of the latest lockout,
  // so a repeat with the same message starts a fresh cooldown.
  useEffect(() => {
    if (!lockoutAt) return undefined;
    setLockedOut(true);
    const timer = setTimeout(() => setLockedOut(false), LOCKOUT_COOLDOWN_MS);
    return () => clearTimeout(timer);
  }, [lockoutAt]);

  // Text and the submit fill use the colour with the better WCAG contrast
  // against the gallery colour, so the gate reads on a light gallery as well
  // as a dark one.
  const textColor = getAccessibleTextColor(galleryColor);
  const errorColor = getErrorAccentColor(galleryColor);
  const message = lockoutMessage || error;
  // React Query v4 reports an in-flight mutation as `isLoading`; `isPending`
  // is the v5 name and is always undefined here.
  const isChecking = tokenMutation.isLoading;
  const title = t("galleryGate.title");
  const titleClass = "font-headline text-3xl sm:text-4xl font-bold leading-tight text-balance break-words";

  return (
    <div
      id="channel-gate"
      className="min-h-[100dvh] w-full flex flex-col items-center justify-center px-6 pt-16 pb-[16vh]"
      style={{ background: galleryColor, color: textColor }}
    >
      {/* Centred on the same axis as the gallery hero, with the gallery's own
          logo (or the Movebooth mark) and headline, so a guest can tell which
          event's gallery they have reached. */}
      <div className="flex flex-col items-center text-center w-full max-w-[420px]">
        <div className="w-full max-w-[240px] mb-8 flex justify-center">
          {logoUrl ? (
            <img src={logoUrl} alt="logo" className="w-full h-auto max-h-[120px] object-contain" />
          ) : (
            <Logo withText={true} fill={textColor} />
          )}
        </div>
        {headline ? (
          <>
            <h1 className={`${titleClass} mb-3`} style={{ color: headlineColor || textColor }}>
              {headline}
            </h1>
            {/* One lead: the event name is bold, the request under it is
                regular weight in the secondary tone. */}
            <h2
              className="font-headline text-lg sm:text-xl font-normal leading-snug text-balance"
              style={{ color: getSecondaryTextColor(galleryColor) }}
            >
              {title}
            </h2>
          </>
        ) : (
          <h1 className={titleClass}>{title}</h1>
        )}
        <form onSubmit={handleSubmit} className="mt-8 mb-8 flex flex-col gap-4 w-full text-left">
          <Input
            name="password"
            placeholder={t("galleryGate.placeholder")}
            value={password}
            label={t("galleryGate.label")}
            type="password"
            bgColor={galleryColor}
            labelInBorder
            invalidRingColor={errorColor}
            aria-invalid={message ? true : undefined}
            aria-describedby={message ? MESSAGE_ID : undefined}
            onChange={(e) => setPassword(e.target.value)}
          />
          {message ? (
            // Sits under the field it is about, and is announced when it appears.
            <p id={MESSAGE_ID} role="alert" className="flex items-start gap-2 font-inter font-normal text-sm leading-5">
              <ExclamationCircleIcon
                className="h-5 w-5 shrink-0"
                style={{ color: errorColor }}
                aria-hidden="true"
              />
              <span>{message}</span>
            </p>
          ) : null}

          <button
            disabled={!password.length || isChecking || lockedOut}
            aria-busy={isChecking || undefined}
            type="submit"
            className={`mt-2 inline-flex items-center justify-center gap-2 w-full min-h-[48px] px-6 py-3 rounded-md font-inter font-bold text-sm uppercase tracking-wider transition-transform duration-200 [transition-timing-function:var(--ease-out)] enabled:hover:outline enabled:hover:outline-1 enabled:hover:outline-offset-2 enabled:active:scale-[0.99] ${
              // Busy, not unavailable: a check in flight keeps the full fill.
              isChecking ? "cursor-wait" : "disabled:cursor-not-allowed disabled:opacity-40"
            } focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4`}
            style={{ background: textColor, color: galleryColor, outlineColor: textColor }}
          >
            {isChecking ? (
              <>
                <span
                  className="h-4 w-4 rounded-full border-2 border-current border-r-transparent animate-spin motion-reduce:animate-none"
                  aria-hidden="true"
                />
                {t("galleryGate.checking")}
              </>
            ) : (
              t("galleryGate.submit")
            )}
          </button>
        </form>
        <LegalLinks color={getAccessibleTextColor(galleryColor)} />
      </div>
    </div>
  );
};

export default GalleryGate;
